import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { backendClient } from '@/lib/api-client';
import type { Docket, DocketCandidate, DocketSyncStatus } from '@/components/dockets/schema';

interface DocketListResponse {
  dockets: Docket[];
}

interface DocketSearchResponse {
  candidates: DocketCandidate[];
}

export interface DocketLinkPayload {
  sourceProvider: string;
  sourceDocketId: string;
}

// Sync statuses where the backend will still make progress (live, or via the
// retry sweeper that re-enqueues partial/failed dockets), so the UI must keep
// polling to reflect self-healing rather than getting stuck on a stale state.
const IN_FLIGHT_SYNC_STATUSES: ReadonlySet<DocketSyncStatus> = new Set([
  'not_started',
  'metadata_synced',
  'syncing',
  'partial',
  'failed',
]);

const POLL_INTERVAL_MS = 4000;

function listKey(matterGuid: string | undefined): string | null {
  return matterGuid ? `/matter/${matterGuid}/dockets` : null;
}

const listFetcher = async (url: string): Promise<Docket[]> => {
  const response = await backendClient.get<DocketListResponse>(url);
  return response.data.dockets ?? [];
};

/**
 * Fetches and mutates a matter's linked dockets against the real provider-agnostic
 * dockets API. Linking only persists metadata synchronously; entries and documents
 * arrive via a background sync, so the list polls while any docket is still syncing.
 */
export function useDockets(matterGuid: string | undefined) {
  const key = listKey(matterGuid);

  const { data, error, isLoading, isValidating, mutate } = useSWR<Docket[]>(key, listFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: (latest) =>
      (latest ?? []).some((docket) => IN_FLIGHT_SYNC_STATUSES.has(docket.syncStatus))
        ? POLL_INTERVAL_MS
        : 0,
  });

  const dockets = useMemo(() => data ?? [], [data]);

  const searchDockets = useCallback(
    async (query: string): Promise<DocketCandidate[]> => {
      if (!matterGuid) return [];
      const response = await backendClient.post<DocketSearchResponse>(
        `/matter/${matterGuid}/dockets/search`,
        { query },
      );
      return response.data.candidates ?? [];
    },
    [matterGuid],
  );

  const linkDocket = useCallback(
    async (payload: DocketLinkPayload): Promise<Docket> => {
      if (!matterGuid) throw new Error('Matter is not available.');
      const response = await backendClient.post<Docket>(
        `/matter/${matterGuid}/dockets/link`,
        payload,
      );
      await mutate();
      return response.data;
    },
    [matterGuid, mutate],
  );

  const unlinkDocket = useCallback(
    async (docketGuid: string): Promise<void> => {
      if (!matterGuid) throw new Error('Matter is not available.');
      await backendClient.delete(`/matter/${matterGuid}/dockets/${docketGuid}`);
      await mutate();
    },
    [matterGuid, mutate],
  );

  const syncDocket = useCallback(
    async (docketGuid: string): Promise<void> => {
      if (!matterGuid) throw new Error('Matter is not available.');
      await backendClient.post(`/matter/${matterGuid}/dockets/${docketGuid}/sync`);
      await mutate();
    },
    [matterGuid, mutate],
  );

  const linkDocument = useCallback(
    async (
      docketGuid: string,
      docketDocumentGuid: string,
      crimsonDocumentGuid: string,
    ): Promise<Docket> => {
      if (!matterGuid) throw new Error('Matter is not available.');
      const response = await backendClient.post<Docket>(
        `/matter/${matterGuid}/dockets/${docketGuid}/documents/${docketDocumentGuid}/link`,
        { documentGuid: crimsonDocumentGuid },
      );
      await mutate();
      return response.data;
    },
    [matterGuid, mutate],
  );

  const unlinkDocument = useCallback(
    async (docketGuid: string, docketDocumentGuid: string): Promise<Docket> => {
      if (!matterGuid) throw new Error('Matter is not available.');
      const response = await backendClient.delete<Docket>(
        `/matter/${matterGuid}/dockets/${docketGuid}/documents/${docketDocumentGuid}/link`,
      );
      await mutate();
      return response.data;
    },
    [matterGuid, mutate],
  );

  return {
    dockets,
    error,
    isLoading,
    isValidating,
    mutate,
    searchDockets,
    linkDocket,
    unlinkDocket,
    syncDocket,
    linkDocument,
    unlinkDocument,
  };
}

export default useDockets;
